import PropTypes from 'prop-types';
import React from 'react';

import TeamTableRow from './TeamTableRow';
import SortIcon from '../../shared/Icons/SortIcon';
import { isProtectedTeamOwner } from '../utils/teamFilterUtils';

const TeamTable = ({
  members,
  selectedMemberIds,
  sortBy,
  sortDirection,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onEdit,
  onDelete,
  onResetPassword,
  csrfToken,
  team,
  onSortChange
}) => {
  const selectableMembers = members.filter(m => !isProtectedTeamOwner(m, team));

  const allSelected = selectableMembers.length > 0 &&
    selectableMembers.every(m => selectedMemberIds.includes(`${m.type_for_ui}-${m.id}`));

  const someSelected = selectableMembers.some(m => selectedMemberIds.includes(`${m.type_for_ui}-${m.id}`));

  const handleSelectAllChange = () => {
    if (allSelected) {
      onDeselectAll();
    } else {
      const memberIds = selectableMembers.map(m => `${m.type_for_ui}-${m.id}`);
      onSelectAll(memberIds);
    }
  };

  const handleSortClick = (columnKey) => {
    let nextDirection = 'asc';

    if (sortBy === columnKey) {
      nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    onSortChange(columnKey, nextDirection);
  };

  if (members.length === 0) {
    return (
      <div className="team-empty-state">
        <div className="team-empty-state-icon">
          <i className="fal fa-users"></i>
        </div>
        <div className="team-empty-state-title">No team members found</div>
        <div className="team-empty-state-text">
          Try adjusting your search or filters
        </div>
      </div>
    );
  }

  return (
    <div className="team-table-wrapper team-table-wrapper--team">
      <table className="team-table">
        <colgroup>
          <col style={{ width: '48px' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '48px' }} />
        </colgroup>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                className="team-checkbox"
                checked={allSelected}
                ref={el => {
                  if (el) {
                    el.indeterminate = someSelected && !allSelected;
                  }
                }}
                onChange={handleSelectAllChange}
                aria-label="Select all"
              />
            </th>
            <th>
              <button
                type="button"
                className="team-sort-header"
                onClick={() => handleSortClick('name')}
              >
                <span>Name</span>
                <SortIcon active={sortBy === 'name'} direction={sortDirection} />
              </button>
            </th>
            <th>
              <button
                type="button"
                className="team-sort-header"
                onClick={() => handleSortClick('role')}
              >
                <span>Role</span>
                <SortIcon active={sortBy === 'role'} direction={sortDirection} />
              </button>
            </th>
            <th>
              <button
                type="button"
                className="team-sort-header"
                onClick={() => handleSortClick('function')}
              >
                <span>Function</span>
                <SortIcon active={sortBy === 'function'} direction={sortDirection} />
              </button>
            </th>
            <th>
              <button
                type="button"
                className="team-sort-header"
                onClick={() => handleSortClick('venue')}
              >
                <span>Venue Access</span>
                <SortIcon active={sortBy === 'venue'} direction={sortDirection} />
              </button>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {members.map((member, index) => (
            <TeamTableRow
              key={`${member.type_for_ui}-${member.id}`}
              teamManageable={member}
              isSelected={selectedMemberIds.includes(`${member.type_for_ui}-${member.id}`)}
              onToggleSelect={onToggleSelect}
              onEdit={onEdit}
              onDelete={onDelete}
              onResetPassword={onResetPassword}
              csrfToken={csrfToken}
              team={team}
              isLastRow={index === members.length - 1}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

TeamTable.propTypes = {
  members: PropTypes.array.isRequired,
  selectedMemberIds: PropTypes.array.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired,
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  onSortChange: PropTypes.func.isRequired
};

export default TeamTable;
